/**
 * Anthropic Claude API Service
 * Handles all interactions with Claude for meal planning conversations
 */

import { apiConfig } from '../config/credentials'
import {
  Recipe,
  Ingredient,
  RecipeDetailRequest,
  RecipeDetailResponse
} from '../types/recipe'
import { MealPlanRequest, AIMealPlanResponse } from '../types/mealPlan'
import { recipeStorageService } from './recipeStorageService'
import { BaseApiService } from './BaseApiService'
import { clientLogger } from '../utils/clientLogger'

class AnthropicService extends BaseApiService {
  constructor() {
    super({
      baseUrl: apiConfig.baseUrl,
      defaultTimeout: 30000, // 30s for Claude API calls
      defaultRetries: 0, // Disable base retry - we use custom retry logic
      enableRetry: false
    })
  }

  /**
   * Test server connectivity
   */
  async testConnectivity(): Promise<boolean> {
    try {
      clientLogger.debug({ url: `${this.baseUrl}/health` }, 'Testing connectivity')
      const response = await fetch(`${this.baseUrl}/health`)
      const isConnected = response.ok
      clientLogger.info({ isConnected, status: response.status }, `Connectivity test: ${isConnected ? 'SUCCESS' : 'FAILED'}`)
      return isConnected
    } catch (error) {
      clientLogger.error({ error: error instanceof Error ? error.message : String(error) }, 'Connectivity test error')
      return false
    }
  }

  /**
   * Send a meal planning request to the server-side /api/claude/meal-plan endpoint.
   * Prompt construction is handled entirely on the backend (issue #43).
   */
  async generateMealPlan(request: MealPlanRequest, retryCount = 0): Promise<AIMealPlanResponse> {
    const maxRetries = 3
    const retryDelay = 1000 * Math.pow(2, retryCount) // Exponential backoff

    try {
      clientLogger.debug({
        attempt: retryCount + 1,
        maxAttempts: maxRetries + 1,
        userId: request.userId,
        sessionId: request.sessionId,
        messageLength: request.message.length,
        historyLength: request.conversationHistory.length,
        baseUrl: this.baseUrl,
        isProd: (import.meta as any).env?.PROD,
        mode: (import.meta as any).env?.MODE
      }, 'Generating meal plan')

      const requestBody = {
        userId: request.userId,
        sessionId: request.sessionId,
        userMessage: request.message,
        conversationHistory: request.conversationHistory
      }

      clientLogger.debug({
        url: `${this.baseUrl}/api/claude/meal-plan`,
        userId: requestBody.userId,
        sessionId: requestBody.sessionId,
        userMessageLength: requestBody.userMessage.length,
        historyLength: requestBody.conversationHistory.length
      }, 'Making meal-plan request')

      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/api/claude/meal-plan`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      })

      clientLogger.debug({ status: response.status }, 'Claude API response status')

      if (!response.ok) {
        const errorText = await response.text()
        clientLogger.error({
          status: response.status,
          statusText: response.statusText,
          errorText
        }, 'Claude API error response')

        // Check if this is a retryable error
        const isRetryable = response.status >= 500 || response.status === 429
        if (isRetryable && retryCount < maxRetries) {
          clientLogger.warn({ status: response.status, retryDelayMs: retryDelay }, 'Retrying meal-plan request')
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          return this.generateMealPlan(request, retryCount + 1)
        }

        throw new Error(`Anthropic API error: ${response.status} ${response.statusText} - ${errorText}`)
      }

      const data = await response.json()
      clientLogger.debug({
        contentLength: data.content?.[0]?.text?.length || 0,
        usage: data.usage
      }, 'Claude API response received')

      const result = this.parseClaudeResponse(data, request.sessionId)
      clientLogger.debug({
        responseLength: result.response.length,
        recipesCount: result.recipes?.length || 0,
        suggestionsCount: result.suggestions.length,
        conversationComplete: result.conversationComplete
      }, 'Parsed meal-plan response')

      return result

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      const errorStack = error instanceof Error ? error.stack : undefined
      const errorName = error instanceof Error ? error.name : 'Unknown'

      clientLogger.error({
        attempt: retryCount + 1,
        error: errorMessage,
        stack: errorStack,
        name: errorName,
        baseUrl: this.baseUrl,
        userId: request.userId,
        sessionId: request.sessionId
      }, 'Error in generateMealPlan')

      // Retry on network errors or timeout
      if (retryCount < maxRetries && (errorName === 'TypeError' || errorMessage.includes('fetch'))) {
        clientLogger.warn({ retryDelayMs: retryDelay }, 'Retrying meal-plan due to network error')
        await new Promise(resolve => setTimeout(resolve, retryDelay))
        return this.generateMealPlan(request, retryCount + 1)
      }

      throw new Error(`Failed to generate meal plan after ${retryCount + 1} attempts: ${errorMessage}`)
    }
  }

  /**
   * Get detailed recipe information from the backend /api/recipes/generate-detail endpoint.
   * Prompt construction and Anthropic API call happen server-side (issue #44).
   */
  async getRecipeDetails(request: RecipeDetailRequest, retryCount = 0): Promise<RecipeDetailResponse> {
    // Check cache first
    clientLogger.debug({ recipeName: request.recipeName }, 'Checking cache for recipe')
    const cachedRecipe = recipeStorageService.getCachedRecipe(request)

    if (cachedRecipe) {
      clientLogger.debug({ recipeName: request.recipeName }, 'Using cached recipe')
      return cachedRecipe
    }

    // Not in cache — delegate to backend
    const maxRetries = 3
    const retryDelay = 1000 * Math.pow(2, retryCount) // Exponential backoff

    try {
      clientLogger.debug({
        attempt: retryCount + 1,
        maxAttempts: maxRetries + 1,
        recipeName: request.recipeName,
        mealType: request.mealType,
        servingSize: request.servingSize,
        sessionId: request.conversationContext.sessionId
      }, 'Fetching recipe from backend')

      const requestBody = {
        userId: request.conversationContext.userId,
        sessionId: request.conversationContext.sessionId,
        recipeName: request.recipeName,
        servingSize: request.servingSize,
        mealType: request.mealType
      }

      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/api/recipes/generate-detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      })

      clientLogger.debug({ status: response.status }, 'Backend recipe detail response status')

      if (!response.ok) {
        const errorText = await response.text()
        clientLogger.error({
          status: response.status,
          statusText: response.statusText,
          errorText
        }, 'Backend recipe detail error')

        const isRetryable = response.status >= 500 || response.status === 429
        if (isRetryable && retryCount < maxRetries) {
          clientLogger.warn({ retryDelayMs: retryDelay }, 'Retrying recipe detail')
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          return this.getRecipeDetails(request, retryCount + 1)
        }

        throw new Error(`Recipe detail API error: ${response.status} ${response.statusText} - ${errorText}`)
      }

      const data = await response.json()

      if (!data.success || !data.recipe) {
        throw new Error(`Recipe detail API returned unsuccessful response: ${JSON.stringify(data)}`)
      }

      const r = data.recipe

      const recipe: Recipe = {
        id: `recipe-${Date.now()}`,
        name: r.name || request.recipeName,
        description: r.description || '',
        ingredients: Array.isArray(r.ingredients) ? r.ingredients.map((ing: any): Ingredient => ({
          name: ing.name || '',
          shoppingName: ing.shoppingName || ing.name || '',
          amount: typeof ing.amount === 'number' ? ing.amount : 1,
          unit: ing.unit || '',
          category: ing.category || 'other'
        })) : [],
        instructions: Array.isArray(r.instructions) ? r.instructions : [],
        prepTime: typeof r.prepTime === 'number' ? r.prepTime : 0,
        cookTime: typeof r.cookTime === 'number' ? r.cookTime : 0,
        servings: typeof r.servings === 'number' ? r.servings : request.servingSize,
        nutrition: r.nutrition != null ? {
          calories: r.nutrition.calories ?? null,
          protein: r.nutrition.protein ?? null,
          carbs: r.nutrition.carbs ?? null,
          fat: r.nutrition.fat ?? null,
          fiber: r.nutrition.fiber ?? null,
          macros_source: r.nutrition.macros_source ?? 'ai_estimated'
        } : null,
        tags: Array.isArray(r.tags) ? r.tags : [],
        mealType: r.mealType || request.mealType
      }

      const result: RecipeDetailResponse = {
        recipe,
        sessionId: data.sessionId || request.conversationContext.sessionId,
        kitchenTools: Array.isArray(r.kitchenTools) ? r.kitchenTools : [],
        cookingTips: Array.isArray(r.cookingTips) ? r.cookingTips : []
      }

      clientLogger.debug({
        recipeName: result.recipe.name,
        ingredientsCount: result.recipe.ingredients.length,
        instructionsCount: result.recipe.instructions.length,
        kitchenToolsCount: result.kitchenTools.length
      }, 'Parsed recipe detail')

      // Store in cache for future use
      clientLogger.debug({ recipeName: request.recipeName }, 'Storing recipe in cache')
      recipeStorageService.storeRecipe(request, result)

      return result

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      const errorName = error instanceof Error ? error.name : 'Unknown'

      clientLogger.error({
        attempt: retryCount + 1,
        error: errorMessage,
        recipeName: request.recipeName,
        sessionId: request.conversationContext.sessionId
      }, 'Error in getRecipeDetails')

      if (retryCount < maxRetries && (errorName === 'TypeError' || errorMessage.includes('fetch'))) {
        clientLogger.warn({ retryDelayMs: retryDelay }, 'Retrying recipe detail due to network error')
        await new Promise(resolve => setTimeout(resolve, retryDelay))
        return this.getRecipeDetails(request, retryCount + 1)
      }

      throw new Error(`Failed to get recipe details after ${retryCount + 1} attempts: ${errorMessage}`)
    }
  }

  /**
   * Parse Claude's response into structured format
   */
  private parseClaudeResponse(data: any, sessionId: string): AIMealPlanResponse {
    const content = data.content[0]?.text || ''
    
    // Check if response contains structured recipe data
    const recipes = this.extractRecipes(content)
    const conversationComplete = this.isConversationComplete(content)
    const suggestions = this.extractSuggestions(content)

    return {
      response: content,
      recipes,
      sessionId,
      suggestions,
      conversationComplete
    }
  }

  /**
   * Extract structured recipes from Claude response
   */
  private extractRecipes(content: string): Recipe[] {
    try {
      // Look for JSON recipe blocks in the response
      const jsonMatch = content.match(/```json\n(.*?)\n```/s)
      if (jsonMatch && jsonMatch[1]) {
        const recipeData = JSON.parse(jsonMatch[1])
        return Array.isArray(recipeData) ? recipeData : [recipeData]
      }
    } catch (error) {
      clientLogger.warn({ error: error instanceof Error ? error.message : String(error) }, 'Could not parse recipes from response')
    }
    return []
  }

  /**
   * Determine if conversation is complete
   */
  private isConversationComplete(content: string): boolean {
    const completionKeywords = [
      'final meal plan',
      'complete recipes',
      'ready to cook',
      'meal plan is ready',
      'shopping list'
    ]
    
    return completionKeywords.some(keyword => 
      content.toLowerCase().includes(keyword)
    )
  }

  /**
   * Extract follow-up suggestions from response
   */
  private extractSuggestions(content: string): string[] {
    // Simple extraction - look for questions or suggestions
    const lines = content.split('\n').filter(line => line.trim())
    return lines
      .filter(line => line.includes('?') || line.includes('suggest') || line.includes('consider'))
      .slice(0, 3) // Max 3 suggestions
  }

  /**
   * Health check for API availability
   */
  async healthCheck(): Promise<boolean> {
    try {
      clientLogger.debug({}, 'Starting API health check')

      const response = await fetch(`${this.baseUrl}/health`)

      clientLogger.debug({ status: response.status, statusText: response.statusText }, 'Health check response')

      if (!response.ok) {
        const errorText = await response.text()
        clientLogger.error({
          status: response.status,
          statusText: response.statusText,
          error: errorText
        }, 'Health check failed')
        return false
      }

      const data = await response.json()
      return data.status === 'ok'
    } catch (error) {
      clientLogger.error({
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      }, 'API health check error')
      return false
    }
  }

  /**
   * Clear recipe cache
   */
  clearRecipeCache(): void {
    recipeStorageService.clearCache()
  }

  /**
   * Get recipe cache statistics
   */
  getRecipeCacheStats(): { count: number; oldestEntry: string | null; newestEntry: string | null; totalSize: number } {
    return recipeStorageService.getCacheStats()
  }

  /**
   * Check if a recipe is already cached
   */
  isRecipeCached(request: RecipeDetailRequest): boolean {
    return recipeStorageService.isRecipeCached(request)
  }

  /**
   * Cleanup expired recipes from cache
   */
  cleanupRecipeCache(): void {
    recipeStorageService.cleanupExpiredRecipes()
  }

  /**
   * Batch fetch recipe details for eager loading
   */
  async batchFetchRecipeDetails(
    requests: RecipeDetailRequest[],
    onProgress?: (completed: number, total: number, recipeName: string) => void
  ): Promise<Map<string, RecipeDetailResponse>> {
    const results = new Map<string, RecipeDetailResponse>()
    
    clientLogger.info({ recipeCount: requests.length }, 'Starting batch fetch')
    
    // First, check what we have cached
    const cachedResults = await recipeStorageService.batchFetchRecipes(requests, onProgress)
    cachedResults.forEach((response, recipeName) => {
      results.set(recipeName, response)
    })
    
    // Then fetch the missing ones
    const uncachedRequests = requests.filter(req => !results.has(req.recipeName))
    
    clientLogger.debug({ cachedCount: results.size, uncachedCount: uncachedRequests.length }, 'Batch fetch cache check complete')
    
    // Fetch uncached recipes in parallel (be careful not to overwhelm the API)
    const batchSize = 3 // Fetch 3 at a time to avoid rate limits
    
    for (let i = 0; i < uncachedRequests.length; i += batchSize) {
      const batch = uncachedRequests.slice(i, i + batchSize)
      
      const batchPromises = batch.map(async (request) => {
        try {
          const response = await this.getRecipeDetails(request)
          results.set(request.recipeName, response)
          
          if (onProgress) {
            onProgress(results.size, requests.length, request.recipeName)
          }
          
          return { success: true, recipeName: request.recipeName }
        } catch (error) {
          clientLogger.error({ recipeName: request.recipeName, error: error instanceof Error ? error.message : String(error) }, 'Failed to fetch recipe details in batch')
          return { success: false, recipeName: request.recipeName, error }
        }
      })
      
      await Promise.all(batchPromises)
      
      // Small delay between batches to be nice to the API
      if (i + batchSize < uncachedRequests.length) {
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    }
    
    clientLogger.info({ loaded: results.size, total: requests.length }, 'Batch fetch complete')
    
    return results
  }

  /**
   * Use backend to consolidate shopping list items intelligently.
   * Prompt engineering and Anthropic API call have moved server-side to
   * POST /api/shopping/consolidate (see issue #45).
   */
  async consolidateShoppingList(shoppingItems: string[]): Promise<{
    items: Array<{
      name: string
      amount: string // DEPRECATED: Legacy field for backward compatibility
      totalQuantity?: string // NEW: Total quantity needed from recipes
      purchaseGuidance?: string // NEW: Purchase recommendation with size
      recipeBreakdown?: string[] // NEW: Per-recipe breakdown
      category: string
    }>
  }> {
    clientLogger.info({ itemCount: shoppingItems.length }, 'Consolidating shopping list via backend')

    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/api/shopping/consolidate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ shoppingItems })
      })

      if (!response.ok) {
        const errorText = await response.text()
        clientLogger.error({
          status: response.status,
          statusText: response.statusText,
          body: errorText,
          itemCount: shoppingItems.length
        }, 'Shopping consolidation HTTP error')
        throw new Error(`Shopping consolidation failed: HTTP ${response.status} ${response.statusText}`)
      }

      const consolidatedData = await response.json()
      clientLogger.info({ itemCount: consolidatedData.items?.length }, 'Shopping consolidation success')

      if (!Array.isArray(consolidatedData.items)) {
        clientLogger.error({ parsedKeys: Object.keys(consolidatedData) }, 'Shopping consolidation response missing items array')
        throw new Error('Shopping consolidation failed: backend response did not contain an items array')
      }

      return consolidatedData

    } catch (error) {
      clientLogger.error({
        error: error instanceof Error ? error.message : String(error),
        itemCount: shoppingItems.length,
        stack: error instanceof Error ? error.stack : undefined
      }, 'Shopping consolidation error')
      throw error
    }
  }
}

export const anthropicService = new AnthropicService()
export default anthropicService